import React, { useState } from 'react';
import { PropTypes } from 'prop-types';
// styles
import styled, { css } from 'styled-components';
import {
  useSoFormStepsApi,
  useSoFormStepsState,
} from 'refactored/features/soForm/SoForm.context';
import FieldError from 'components/form/FieldError';
import { useTranslation } from 'react-i18next';

//

const InputFrame = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  height: 40px;
  flex: none;
  order: 0;
  flex-grow: 1;
  position: relative;
`;

const SoFormLoadSizeInputLabel = styled.div`
  height: 16px;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  display: flex;
  align-items: center;
  color: var(--grey500);
  flex: none;
  order: 0;
  flex-grow: 0;
`;

const SoFormLoadSizeInputSubFrame = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px;
  gap: 8px;
  height: 24px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
`;

const SoFormLoadSizeInputContent = styled.input`
  height: 24px;
  width: 50px;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  display: flex;
  align-items: center;
  color: var(--grey900);
  flex: none;
  order: 0;
  flex-grow: 1;
  outline: none;
  border: none;
  background: transparent;
`;

const SoFormLoadSizeInputSubfixFrame = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 10px;
  width: 17px;
  height: 16px;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

const SoFormLoadSizeInputSubfix = styled.div`
  width: 17px;
  height: 16px;

  font-family: 'Roboto';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  display: flex;
  align-items: center;
  color: var(--grey500);
  flex: none;
  order: 0;
  flex-grow: 0;
`;

const SoFormLoadSizeInput = styled(({ className, inputData, load }) => {
  // state
  const stepsApi = useSoFormStepsApi();
  const stepsState = useSoFormStepsState();

  //   value
  const field = inputData.field;
  const value = load[inputData.field];
  const displayedValue = value >= 1 ? value : '';

  //   focus
  const [isFocused, setIsFocused] = useState(false);

  //   errors
  const errors = stepsState.configuration.errors;

  const hasEmptyError = !!errors.fieldsWithEmptyError.find(
    (e) => e.field === field && e.loadKey === load.loadKey
  );
  const showEmptyError =
    stepsState.configuration.showConfigurationErrors && hasEmptyError;

  const hasLogicError = !!errors.fieldsWithInvalidError.find(
    (e) => e.field === field && e.loadKey === load.loadKey
  );
  const showLogicError = hasLogicError;

  const showError = showEmptyError || showLogicError;

  //   classnames

  const focusClassName = isFocused ? 'isFocused' : '';
  const errorClassName = showError ? 'hasError' : '';
  const defaultClassName = inputData.isDefault ? 'isDefault' : '';
  const totalClassName = `${className} ${errorClassName} ${focusClassName} ${defaultClassName}`;

  const updateSizeInput = (e) => {
    stepsApi.setLoadField({ load, field, value: e.target.value });
  };

  const ignoreNotNumbers = (e) => {
    if (
      !(
        (e.keyCode > 95 && e.keyCode < 106) ||
        (e.keyCode > 47 && e.keyCode < 58) ||
        e.keyCode === 8 ||
        e.keyCode === 9
      )
    ) {
      e.preventDefault();
    }
  };

  const { t } = useTranslation('Catalog');

  let errorText = '';
  if (field === 'item_length')
    errorText = t('configuration.loads.errors.item_length');
  else if (field === 'item_width')
    errorText = t('configuration.loads.errors.item_width');
  else if (field === 'item_height')
    errorText = t('configuration.loads.errors.item_height');
  else if (field === 'total_weight')
    errorText = t('configuration.loads.errors.total_weight');

  return (
    <div className={totalClassName}>
      <InputFrame>
        {showLogicError && isFocused && <FieldError>{errorText}</FieldError>}
        <SoFormLoadSizeInputLabel>{inputData.label}</SoFormLoadSizeInputLabel>
        <SoFormLoadSizeInputSubFrame>
          <SoFormLoadSizeInputContent
            type="number"
            step={1}
            value={displayedValue}
            onChange={updateSizeInput}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={ignoreNotNumbers}
          ></SoFormLoadSizeInputContent>
          <SoFormLoadSizeInputSubfixFrame>
            <SoFormLoadSizeInputSubfix>
              {inputData.units}
            </SoFormLoadSizeInputSubfix>
          </SoFormLoadSizeInputSubfixFrame>
        </SoFormLoadSizeInputSubFrame>
      </InputFrame>
    </div>
  );
})``;

SoFormLoadSizeInput.propTypes = {
  className: PropTypes.string,
};

const regularCSS = css`
  box-sizing: border-box;
  flex: 1;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-end;
  padding: 6px 12px 2px 16px;
  gap: 12px;

  /* width: 136px; */
  height: 48px;

  border-width: 1px 1px 1px 4px;
  border-style: solid;
  border-color: var(--grey300);
  border-radius: 4px;

  flex: none;
  order: 0;
  flex-grow: 1;

  &.isFocused {
    border-left-color: var(--primary500);
  }
  &.hasError {
    border-bottom: 4px solid var(--error700);
  }
  &.isDefault {
    background: var(--grey200);
  }
`;

export default styled(SoFormLoadSizeInput)`
  ${regularCSS}
`;
